import React, { createContext, useContext, ReactNode } from "react";
import { toast } from "@/components/ui/toast";
import { Check, AlertCircle, AlertTriangle, Info } from "lucide-react";

// Toast message types
type ToastType = "success" | "error" | "warning" | "info";

// Toast context interface
interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  dismiss: (toastId?: string) => void;
}

// Create the context with default values
const ToastContext = createContext<ToastContextType>({
  showToast: () => {},
  dismiss: () => {},
});

// Export the hook for using the context
export const useToast = () => useContext(ToastContext);

// Provider component
interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  /**
   * Show a toast with the specified type and duration
   * @param message The message to display
   * @param type The toast type (success, error, warning, info)
   * @param duration Duration in milliseconds before auto dismiss (defaults to 3000ms)
   */
  const showToast = (
    message: string,
    type: ToastType = "info",
    duration?: number
  ) => {
    // Create type-specific styles
    const classNames = {
      success: {
        toast: "!bg-status-bg-success !border !border-status-bd-success",
        title: "!text-status-fg-success",
        icon: "text-status-icon-success",
      },
      error: {
        toast: "!bg-status-bg-error !border !border-status-bd-error",
        title: "!text-status-fg-error",
        icon: "text-status-icon-error",
      },
      warning: {
        toast: "!bg-status-bg-caution !border !border-status-bd-caution",
        title: "!text-status-fg-caution",
        icon: "text-status-icon-caution",
      },
      info: {
        toast: "!bg-white !border !border-bd-form",
        title: "!text-tx-default",
        icon: "text-tx-default",
      },
    };

    const icons = {
      success: <Check size={18} />,
      error: <AlertCircle size={18} />,
      warning: <AlertTriangle size={18} />,
      info: <Info size={18} />,
    };

    // sometimes frontend thinks duration is in seconds, so convert to milliseconds
    if (duration && duration < 500) {
      duration = duration * 1000;
    }

    toast(message, {
      icon: icons[type],
      duration: duration ?? 3000,
      classNames: classNames[type],
    });
  };

  // Dismiss a toast by ID or all toasts if no ID is provided
  const dismiss = (toastId?: string) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  };

  return (
    <ToastContext.Provider
      value={{
        showToast,
        dismiss,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};
